import { useEffect, useState } from "react";

import type { Brand, PaginationMeta, Product } from "../types/marketplace";
import { formatNumber } from "../utils/formatters";
import LoadingSpinner from "./LoadingSpinner";
import ProductGrid from "./ProductGrid";

interface BrandViewProps {
  brand: Brand;
  categories: string[];
  comparedProductIds?: string[];
  loading: boolean;
  meta: PaginationMeta;
  onBack: () => void;
  onCompareToggle?: (product: Product) => void;
  onProductsRequest: (clientId: number, page: number, category?: string) => void;
  onProductSelect: (product: Product) => void;
  products: Product[];
}

const allCategoriesLabel = "Todas";

function BrandView({
  brand,
  categories,
  comparedProductIds = [],
  loading,
  meta,
  onBack,
  onCompareToggle,
  onProductsRequest,
  onProductSelect,
  products
}: BrandViewProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>(allCategoriesLabel);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const totalPages = Math.max(1, meta.lastPage);
  const visibleProducts = brand.clientId
    ? products.filter((product) => product.clientId === brand.clientId)
    : products;

  useEffect(() => {
    setSelectedCategory(allCategoriesLabel);
    setCurrentPage(1);
  }, [brand.clientId, brand.name]);

  useEffect(() => {
    if (!brand.clientId) {
      return;
    }

    onProductsRequest(
      brand.clientId,
      currentPage,
      selectedCategory === allCategoriesLabel ? undefined : selectedCategory
    );
  }, [brand.clientId, currentPage, onProductsRequest, selectedCategory]);

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  return (
    <main className="brand-view">
      <section className="container brand-hero">
        <button className="ghost-button" onClick={onBack} type="button">
          Volver
        </button>

        <div className="brand-hero-logo" style={{ backgroundColor: brand.color }}>
          {brand.logo ? (
            <img src={brand.logo} alt={brand.name} decoding="async" loading="lazy" />
          ) : (
            brand.letter
          )}
        </div>

        <div>
          <small>{brand.category}</small>
          <h1>{brand.name}</h1>
          <p>
            {formatNumber(meta.total)} productos disponibles
            {categories.length > 0 ? ` - ${formatNumber(categories.length)} categorías` : ""}
          </p>
        </div>
      </section>

      <section className="container brand-products-box">
        <div className="brand-filter-bar">
          <button
            className={selectedCategory === allCategoriesLabel ? "selected" : ""}
            onClick={() => handleCategoryChange(allCategoriesLabel)}
            type="button"
          >
            {allCategoriesLabel}
          </button>

          {categories.map((category) => (
            <button
              className={selectedCategory === category ? "selected" : ""}
              key={category}
              onClick={() => handleCategoryChange(category)}
              type="button"
            >
              {category}
            </button>
          ))}
        </div>

        {loading ? (
          <LoadingSpinner label="Cargando productos de la tienda..." />
        ) : visibleProducts.length > 0 ? (
          <ProductGrid
            comparedProductIds={comparedProductIds}
            onCompareToggle={onCompareToggle}
            onProductSelect={onProductSelect}
            products={visibleProducts}
          />
        ) : (
          <p className="empty-state">No hay productos para esta categoría.</p>
        )}

        <div className="pagination-bar">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
            type="button"
          >
            Anterior
          </button>
          <span>
            Página {formatNumber(currentPage)} de {formatNumber(totalPages)}
          </span>
          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
            type="button"
          >
            Siguiente
          </button>
        </div>
      </section>
    </main>
  );
}

export default BrandView;
